"use client";

const MARKDOWN_BLOB_IMAGE_PATTERN = /!\[([^\]]*)\]\((blob:[^)]+)\)/g;

function unique<T>(values: T[]): T[] {
  return [...new Set(values)];
}

export function extractMarkdownBlobImageUrls(content: string): string[] {
  if (!content.includes("blob:")) return [];
  return unique([...content.matchAll(MARKDOWN_BLOB_IMAGE_PATTERN)].map((match) => match[2]));
}

interface UploadBlockEditorImagesOptions {
  content: string;
  imageFileMap: Map<string, File>;
  uploadEndpoint: string;
  shouldContinue?: () => boolean;
}

export class BlockEditorImageUploadCanceledError extends Error {
  constructor() {
    super("저장 가능 상태가 바뀌어 이미지 업로드를 중단했습니다.");
    this.name = "BlockEditorImageUploadCanceledError";
  }
}

export async function uploadBlockEditorImages({
  content,
  imageFileMap,
  uploadEndpoint,
  shouldContinue = () => true,
}: UploadBlockEditorImagesOptions): Promise<string> {
  const assertCanContinue = () => {
    if (!shouldContinue()) throw new BlockEditorImageUploadCanceledError();
  };
  assertCanContinue();
  if (!content.includes("blob:")) {
    return content;
  }

  const blobUrls = extractMarkdownBlobImageUrls(content);
  if (blobUrls.length === 0) return content;

  let processedContent = content;
  const uploadedByBlobUrl = new Map<string, string>();

  for (const blobUrl of blobUrls) {
    assertCanContinue();
    const file = imageFileMap.get(blobUrl);
    if (!file) {
      throw new Error(
        "이미지 파일 정보가 없어 저장할 수 없습니다. 페이지를 새로고침한 뒤 이미지를 다시 첨부해주세요."
      );
    }
    if (uploadedByBlobUrl.has(blobUrl)) continue;

    const formData = new FormData();
    formData.append("file", file);

    const response = await fetch(uploadEndpoint, {
      method: "POST",
      body: formData,
    });
    assertCanContinue();
    const result = await response.json();
    assertCanContinue();

    if (!response.ok || !result.success || !result.url) {
      throw new Error(result.error || "이미지 업로드 실패");
    }

    uploadedByBlobUrl.set(blobUrl, result.url);
  }

  for (const [blobUrl, uploadedUrl] of uploadedByBlobUrl) {
    processedContent = processedContent.split(blobUrl).join(uploadedUrl);
  }

  return processedContent;
}
